/**
 * The Workflow Builder's editing rules (web builder; docs/features/workflows.md "Builder (web)"), on top of
 * logic/workflows.ts (validation, variables, templates):
 *
 * - **Triggers:** switching the trigger type keeps the channel where both have one; a new schedule starts at the next
 *   full hour in my zone (`switchTrigger`). Schedules are edited as a local day and time in their zone.
 * - **Template fields** show each `{{ref}}` as one chip character (U+FFFC, like the composer's mentions): edits arrive
 *   in that "chip text" and are mapped back onto the template (`editTemplate`). Chips read like Slack's
 *   ("Person who used this workflow", "Form 1 › Question").
 * - **Problems** from `workflowProblems` are split into the workflow's own and each step's, with their messages.
 * - **List:** the workflows I manage, last edited first, each published / draft / with unpublished changes.
 * - **App steps:** "Add step → app step" lists the installed apps that declare workflow steps (Slack's functions) and
 *   their steps (`appStepChoices`); picking one makes an `app_step` with one empty template per declared input and
 *   the declared outputs (`appStepFromDeclaration`). A saved app step whose app or declaration is gone, or whose
 *   required inputs are empty, has `appStepIssues` (required inputs keep it from saving, like Slack).
 */
import type { App, AppWorkflowStep, ConditionOp, Workflow, WorkflowDefinition, WorkflowSchedule, WorkflowStep, WorkflowStepType, WorkflowTrigger, WorkflowTriggerType } from "@bundle/contracts";
import { msg, type MessageDescriptor, type MessageKey } from "../i18n.ts";
import { fromLocal, localParts } from "./calendar.ts";
import type { WorkspaceCache } from "./workspace.ts";
import { availableVariables, templateSegments, variableToken, type WorkflowProblem, type WorkflowVariable } from "./workflows.ts";

/** The chip character a variable takes in a template field (one UTF-16 unit, like the composer's mentions). */
export const CHIP = "￼";

export const TRIGGER_TYPES: readonly WorkflowTriggerType[] = ["link", "member_joined", "reaction", "schedule", "webhook"];
export const STEP_TYPES: readonly WorkflowStepType[] = ["send_message", "form", "add_reaction", "create_page", "create_row", "add_to_channel", "delay", "app_step"];
export const CONDITION_OPS: readonly ConditionOp[] = ["is", "is_not", "contains", "is_empty", "is_not_empty", "greater_than", "less_than"];

const STEP_KEYS = {
  send_message: "workflow.step.sendMessage",
  form: "workflow.step.form",
  add_reaction: "workflow.step.addReaction",
  create_page: "workflow.step.createPage",
  create_row: "workflow.step.createRow",
  add_to_channel: "workflow.step.addToChannel",
  delay: "workflow.step.delay",
  app_step: "workflow.step.appStep",
} as const satisfies Record<WorkflowStepType, MessageKey>;

const TRIGGER_KEYS = {
  link: ["workflow.trigger.link", "workflow.trigger.link.help"],
  member_joined: ["workflow.trigger.memberJoined", "workflow.trigger.memberJoined.help"],
  reaction: ["workflow.trigger.reaction", "workflow.trigger.reaction.help"],
  schedule: ["workflow.trigger.schedule", "workflow.trigger.schedule.help"],
  webhook: ["workflow.trigger.webhook", "workflow.trigger.webhook.help"],
} as const satisfies Record<WorkflowTriggerType, readonly [MessageKey, MessageKey]>;

const OP_KEYS = {
  is: "workflow.condition.op.is",
  is_not: "workflow.condition.op.isNot",
  contains: "workflow.condition.op.contains",
  is_empty: "workflow.condition.op.isEmpty",
  is_not_empty: "workflow.condition.op.isNotEmpty",
  greater_than: "workflow.condition.op.greaterThan",
  less_than: "workflow.condition.op.lessThan",
} as const satisfies Record<ConditionOp, MessageKey>;

/** A step type's name ("Send a message"; the "Add step" menu). */
export function stepTypeLabel(type: WorkflowStepType): MessageDescriptor {
  return msg(STEP_KEYS[type]);
}

/** A trigger type's name ("From a link in a channel") and what it does. */
export function triggerLabel(type: WorkflowTriggerType): MessageDescriptor {
  return msg(TRIGGER_KEYS[type][0]);
}

export function triggerHelp(type: WorkflowTriggerType): MessageDescriptor {
  return msg(TRIGGER_KEYS[type][1]);
}

/** A condition's comparison ("is not"). */
export function conditionOpLabel(op: ConditionOp): MessageDescriptor {
  return msg(OP_KEYS[op]);
}

/** A new workflow: a link in `channelId` (Slack starts from "From a link in Slack"), no steps yet. */
export function blankDefinition(channelId = ""): WorkflowDefinition {
  return { name: "", trigger: { type: "link", channelId }, steps: [] };
}

/** The next full hour after `now` (a new schedule's first run). */
export function nextFullHour(now: number): number {
  const hour = 60 * 60 * 1000;
  return Math.floor(now / hour) * hour + hour;
}

/** A trigger of another type, keeping the channel when both have one. */
export function switchTrigger(prev: WorkflowTrigger, type: WorkflowTriggerType, now: number, timeZone: string): WorkflowTrigger {
  if (prev.type === type) return prev;
  const channelId = "channelId" in prev ? prev.channelId : "";
  switch (type) {
    case "link":
      return { type, channelId };
    case "member_joined":
      return { type, channelId };
    case "reaction":
      return { type, channelId, emoji: "✅" };
    case "schedule":
      return { type, schedule: { startAt: nextFullHour(now), timeZone, repeat: "none" } };
    case "webhook":
      return { type, variables: [] };
  }
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** A schedule's first run as its zone's day ("YYYY-MM-DD") and time ("HH:MM"). */
export function scheduleParts(s: WorkflowSchedule): { date: string; time: string } {
  const p = localParts(s.startAt, s.timeZone);
  return { date: `${p.year}-${pad(p.month)}-${pad(p.day)}`, time: `${pad(p.hour)}:${pad(p.minute)}` };
}

/** The schedule with its first run moved to `date` / `time` (in its zone); bad input leaves it as is. */
export function withScheduleParts(s: WorkflowSchedule, date: string, time: string): WorkflowSchedule {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const t = /^(\d{1,2}):(\d{2})$/.exec(time);
  if (d === null || t === null) return s;
  return { ...s, startAt: fromLocal(Number(d[1]), Number(d[2]), Number(d[3]), Number(t[1]), Number(t[2]), s.timeZone) };
}

/** The schedule moved to another zone, keeping its wall-clock day and time. */
export function withTimeZone(s: WorkflowSchedule, timeZone: string): WorkflowSchedule {
  const { date, time } = scheduleParts(s);
  return withScheduleParts({ ...s, timeZone }, date, time);
}

/** Time slots every 15 minutes ("00:00" … "23:45"), the schedule's time menu. */
export function timeSlots(): string[] {
  const out: string[] = [];
  for (let m = 0; m < 24 * 60; m += 15) out.push(`${pad(Math.floor(m / 60))}:${pad(m % 60)}`);
  return out;
}

/** A weekly schedule's days, toggling `day` (never empty: the last day stays). */
export function toggleWeekday(days: readonly number[], day: number): number[] {
  if (days.includes(day)) return days.length === 1 ? [...days] : days.filter((d) => d !== day);
  return [...days, day].toSorted((a, b) => a - b);
}

/** A wait of `count` units in minutes (at least 1). */
export function delayMinutes(unit: "minutes" | "hours" | "days", count: number): number {
  const n = Math.max(1, Math.floor(Number.isFinite(count) ? count : 1));
  return unit === "days" ? n * 24 * 60 : unit === "hours" ? n * 60 : n;
}

/** The empty checks take no comparison value. */
export function conditionNeedsCompare(op: ConditionOp): boolean {
  return op !== "is_empty" && op !== "is_not_empty";
}

// ---------- Template fields (chips) ----------

/** A template as the field shows it: text with one CHIP per variable, and those variables' refs in order. */
export function chipText(template: string): { text: string; refs: string[] } {
  let text = "";
  const refs: string[] = [];
  for (const s of templateSegments(template)) {
    if (s.kind === "text") text += s.text.replaceAll(CHIP, "");
    else {
      text += CHIP;
      refs.push(s.ref);
    }
  }
  return { text, refs };
}

/** The template's pieces, one per chip-text unit (a character, or a whole `{{ref}}`). */
function units(template: string): string[] {
  const out: string[] = [];
  for (const s of templateSegments(template)) {
    if (s.kind === "variable") out.push(variableToken(s.ref));
    else for (const ch of s.text.replaceAll(CHIP, "")) out.push(...ch.split(""));
  }
  return out;
}

/** An edit in the field ([start, end) of the chip text replaced by `inserted`) applied to the template. */
export function editTemplate(template: string, start: number, end: number, inserted: string): string {
  const u = units(template);
  const a = Math.max(0, Math.min(start, u.length));
  const b = Math.max(a, Math.min(end, u.length));
  return u.slice(0, a).join("") + inserted.replaceAll(CHIP, "") + u.slice(b).join("");
}

/** Inserts a variable at chip-text offset `at` (replacing [at, end)); returns the template and the caret after it. */
export function insertVariable(template: string, at: number, end: number, ref: string): { template: string; caret: number } {
  return { template: editTemplate(template, at, end, variableToken(ref)), caret: Math.min(at, end) + 1 };
}

/** Text a label shows: a message to translate, or the author's own words (a form field's label, a step's title). */
export type Label = MessageDescriptor | string;

/**
 * What a variable chip says: the trigger's variables by role ("Person who used this workflow"); a step's outputs by
 * name (form fields by their label) after the step ("1. Form › Question"). `known` is false for a ref the step can't
 * use (a red chip).
 */
export type VariableChip = { ref: string; known: boolean; label: Label; step?: { n: number; title: Label }; type?: WorkflowVariable["type"] };

/** A step's name in the list and in chips: its own title (forms, app steps) or its kind ("Send a message"). */
export function stepTitle(step: WorkflowStep): Label {
  if ((step.type === "app_step" || step.type === "form") && step.title.trim() !== "") return step.title;
  return stepTypeLabel(step.type);
}

function triggerVarLabel(trigger: WorkflowTrigger, name: string): Label {
  switch (name) {
    case "user":
      return trigger.type === "member_joined" ? msg("workflow.var.trigger.joined") : trigger.type === "reaction" ? msg("workflow.var.trigger.reactor") : msg("workflow.var.trigger.user");
    case "channel":
      return msg("workflow.var.trigger.channel");
    case "message":
      return msg("workflow.var.trigger.message");
    case "message_text":
      return msg("workflow.var.trigger.messageText");
    case "message_author":
      return msg("workflow.var.trigger.messageAuthor");
    case "emoji":
      return msg("workflow.var.trigger.emoji");
    case "time":
      return msg("workflow.var.trigger.time");
    default:
      return name;
  }
}

function outputLabel(step: WorkflowStep, name: string): Label {
  if (step.type === "form") {
    if (name === "user") return msg("workflow.var.output.submitter");
    return step.fields.find((f) => f.id === name)?.label ?? name;
  }
  if (name === "message" || name === "channel" || name === "page" || name === "row") return msg(`workflow.var.output.${name}`);
  return name;
}

/** The chip for `ref` in a field of step `stepId` (absent: after the last step). */
export function variableChip(def: Pick<WorkflowDefinition, "trigger" | "steps">, ref: string, stepId?: string): VariableChip {
  const known = availableVariables(def, stepId).find((v) => v.ref === ref);
  const any = known ?? availableVariables(def).find((v) => v.ref === ref);
  if (any === undefined) return { ref, known: false, label: ref };
  const base: Pick<VariableChip, "ref" | "known" | "type"> = known === undefined ? { ref, known: false } : { ref, known: true, type: any.type };
  if (any.source === "trigger") return { ...base, label: triggerVarLabel(def.trigger, any.name) };
  const index = def.steps.findIndex((s) => s.id === any.source);
  const step = def.steps[index]!;
  return { ...base, label: outputLabel(step, any.name), step: { n: index + 1, title: stepTitle(step) } };
}

// ---------- Problems ----------

/** The workflow's own problems (name, trigger, no steps) and each step's, by step id. */
export function splitProblems(problems: readonly WorkflowProblem[]): { workflow: WorkflowProblem[]; steps: Record<string, WorkflowProblem[]> } {
  const workflow: WorkflowProblem[] = [];
  const steps: Record<string, WorkflowProblem[]> = {};
  for (const p of problems) {
    if ("stepId" in p) (steps[p.stepId] ??= []).push(p);
    else workflow.push(p);
  }
  return { workflow, steps };
}

/** What a problem says in the builder. */
export function problemMessage(p: WorkflowProblem): MessageDescriptor {
  switch (p.code) {
    case "duplicateVariable":
      return msg("workflow.problem.duplicateVariable", { key: p.key });
    case "unknownVariable":
      return msg("workflow.problem.unknownVariable", { ref: p.ref });
    case "duplicateField":
    case "noOptions":
      return msg(`workflow.problem.${p.code}`, { field: p.fieldId });
    default:
      return msg(`workflow.problem.${p.code}`);
  }
}

const KEY = /^[a-z][a-z0-9_]{0,39}$/;
const blank = (s: string | undefined) => s === undefined || s.trim() === "";

/** A step whose required fields are empty: it can't be saved yet (Slack keeps a step's Save off until they're filled). */
function stepIncomplete(s: WorkflowStep): boolean {
  if (s.condition !== undefined && blank(s.condition.value)) return true;
  switch (s.type) {
    case "send_message":
      return blank(s.text) || (s.to.kind === "channel" && blank(s.to.channelId)) || (s.to.kind === "user" && blank(s.to.userId)) || (s.to.kind === "variable" && blank(s.to.ref));
    case "form":
      return blank(s.title) || s.fields.length === 0 || s.fields.some((f) => blank(f.label) || !KEY.test(f.id) || (f.options ?? []).some((o) => blank(o)));
    case "add_reaction":
      return blank(s.message) || blank(s.emoji);
    case "create_page":
      return blank(s.title);
    case "create_row":
      return blank(s.databaseId) || blank(s.title);
    case "add_to_channel":
      return blank(s.channelId) || blank(s.user);
    case "delay":
      return s.minutes < 1;
    case "app_step":
      return blank(s.appId) || blank(s.callbackId) || blank(s.title) || s.outputs.some((o) => !KEY.test(o.name));
  }
}

/**
 * What keeps a draft from being saved at all (the server refuses empty required fields): no name, an incomplete
 * trigger (`trigger`), or steps with empty required fields (their ids). Publishing also needs `workflowProblems` empty.
 */
export function incomplete(def: WorkflowDefinition): { name: boolean; trigger: boolean; steps: string[] } {
  const t = def.trigger;
  const trigger =
    ("channelId" in t && blank(t.channelId)) ||
    (t.type === "reaction" && blank(t.emoji)) || (t.type === "schedule" && blank(t.schedule.timeZone)) || (t.type === "webhook" && t.variables.some((v) => !KEY.test(v.key)));
  return { name: blank(def.name), trigger, steps: def.steps.filter(stepIncomplete).map((s) => s.id) };
}

/** Nothing keeps the draft from being saved. */
export function canSaveDraft(def: WorkflowDefinition): boolean {
  const i = incomplete(def);
  return !i.name && !i.trigger && i.steps.length === 0;
}

// ---------- App steps ----------

export type AppStep = Extract<WorkflowStep, { type: "app_step" }>;

/** An installed app that offers workflow steps, with its steps by title (the "Add step → app step" picker). */
export type AppStepChoice = { appId: string; appName: string; iconUrl?: string; steps: AppWorkflowStep[] };

/** Installed apps that declare workflow steps, by name; each one's steps by title. */
export function appStepChoices(apps: readonly App[]): AppStepChoice[] {
  return apps
    .filter((a) => (a.workflowSteps ?? []).length > 0)
    .map((a) => {
      const c: AppStepChoice = { appId: a.id, appName: a.name, steps: (a.workflowSteps ?? []).toSorted((x, y) => x.title.localeCompare(y.title) || x.callbackId.localeCompare(y.callbackId)) };
      if (a.iconUrl !== undefined) c.iconUrl = a.iconUrl;
      return c;
    })
    .toSorted((a, b) => a.appName.localeCompare(b.appName) || a.appId.localeCompare(b.appId));
}

/** A new `app_step` for a declared step: its title, one empty template per declared input, its outputs. */
export function appStepFromDeclaration(stepId: string, app: Pick<App, "id">, decl: AppWorkflowStep): AppStep {
  return {
    type: "app_step",
    id: stepId,
    appId: app.id,
    callbackId: decl.callbackId,
    title: decl.title,
    inputs: Object.fromEntries(decl.inputs.map((i) => [i.name, ""])),
    outputs: decl.outputs.map((o) => ({ name: o.name, type: o.type })),
  };
}

/** The declaration an app step was made from, when its app is installed and still declares it. */
export function appStepDeclaration(step: Pick<AppStep, "appId" | "callbackId">, apps: readonly App[]): AppWorkflowStep | undefined {
  return apps.find((a) => a.id === step.appId)?.workflowSteps?.find((d) => d.callbackId === step.callbackId);
}

export type AppStepIssue = { code: "appMissing" } | { code: "stepMissing" } | { code: "requiredInput"; name: string; label: string };

/**
 * What's wrong with an app step given the installed apps: the app isn't installed here (`appMissing`), it no longer
 * declares the step (`stepMissing`), or a required input is empty (`requiredInput`, in declared order). Only call it
 * once the apps are loaded.
 */
export function appStepIssues(step: AppStep, apps: readonly App[]): AppStepIssue[] {
  const app = apps.find((a) => a.id === step.appId);
  if (app === undefined) return [{ code: "appMissing" }];
  const decl = app.workflowSteps?.find((d) => d.callbackId === step.callbackId);
  if (decl === undefined) return [{ code: "stepMissing" }];
  return decl.inputs.filter((i) => i.required === true && blank(step.inputs[i.name])).map((i) => ({ code: "requiredInput", name: i.name, label: i.label }));
}

/** What an app step issue says in the builder. */
export function appStepIssueMessage(issue: AppStepIssue): MessageDescriptor {
  switch (issue.code) {
    case "appMissing":
      return msg("workflow.app.issue.appMissing");
    case "stepMissing":
      return msg("workflow.app.issue.stepMissing");
    case "requiredInput":
      return msg("workflow.app.issue.requiredInput", { field: issue.label });
  }
}

/** Whether issues keep the step from saving: only empty required inputs (a missing app can still be removed later). */
export function appStepBlocksSave(issues: readonly AppStepIssue[]): boolean {
  return issues.some((i) => i.code === "requiredInput");
}

export type AppWorkflowStepsProblem =
  | { code: "duplicateCallbackId"; callbackId: string }
  | { code: "duplicateInput"; callbackId: string; name: string }
  | { code: "duplicateOutput"; callbackId: string; name: string };

/** The first thing wrong with an app's declared steps (the API refuses it; Settings → Apps says it), or undefined. */
export function appWorkflowStepsProblem(steps: readonly AppWorkflowStep[]): AppWorkflowStepsProblem | undefined {
  const ids = new Set<string>();
  for (const s of steps) {
    if (ids.has(s.callbackId)) return { code: "duplicateCallbackId", callbackId: s.callbackId };
    ids.add(s.callbackId);
    const inputs = new Set<string>();
    for (const i of s.inputs) {
      if (inputs.has(i.name)) return { code: "duplicateInput", callbackId: s.callbackId, name: i.name };
      inputs.add(i.name);
    }
    const outputs = new Set<string>();
    for (const o of s.outputs) {
      if (outputs.has(o.name)) return { code: "duplicateOutput", callbackId: s.callbackId, name: o.name };
      outputs.add(o.name);
    }
  }
  return undefined;
}

// ---------- Pickers ----------

/** Channels a workflow can use: ones I'm in (publishing needs it), not archived, by name. */
export function workflowChannels(cache: Pick<WorkspaceCache, "channels" | "memberships">): { id: string; name: string; private: boolean }[] {
  return Object.values(cache.channels)
    .filter((c) => (c.kind === "public" || c.kind === "private") && !c.archived && cache.memberships[c.id] !== undefined)
    .map((c) => ({ id: c.id, name: c.name, private: c.kind === "private" }))
    .toSorted((a, b) => a.name.localeCompare(b.name));
}

/** People to pick (message recipients, managers): active people, not agents; `membersOnly` leaves guests out. */
export function workflowPeople(cache: Pick<WorkspaceCache, "members">, membersOnly = false): { id: string; name: string }[] {
  return Object.values(cache.members)
    .filter((m) => !m.deactivated && m.kind !== "agent" && (!membersOnly || m.role !== "guest"))
    .map((m) => ({ id: m.userId, name: m.displayName }))
    .toSorted((a, b) => a.name.localeCompare(b.name));
}

// ---------- The list ----------

export type WorkflowListStatus = "published" | "changed" | "draft";

/** Published, published with unpublished changes, or a draft never published / unpublished. */
export function workflowStatus(wf: Pick<Workflow, "published" | "changed">): WorkflowListStatus {
  if (!wf.published) return "draft";
  return wf.changed === true ? "changed" : "published";
}

/** The workflows I manage, last edited first. */
export function managedWorkflows(list: readonly Workflow[]): Workflow[] {
  return list.filter((w) => w.canManage).toSorted((a, b) => b.updatedAt - a.updatedAt || a.definition.name.localeCompare(b.definition.name));
}

/** Two definitions are the same (the editor's "unsaved changes"). */
export function sameDefinition(a: WorkflowDefinition, b: WorkflowDefinition): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
